

import { useStateManager, useRoute } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import DateSelect from 'components/custom-ui/DateSelect';
import { FormContainer, FormRowView } from 'components/shared';
import InfoAlertView from "components/shared/views/InfoAlertView";
import { PrimaryButton, SecondaryButton, UIAmountInput, UITextInput } from 'components/ui';
import React from 'react';
import { SVGAsset } from "res";
import TASPositivePayViewChecksNoResultsContent from "../TASPositivePayViewChecksNoResultsContent";
import TASPositivePayViewChecksDesktopTable from "../TASPositivePayViewChecksDesktopTable";

/**
 * @function TASPositivePayViewChecksDesktopFormView
 * @return {React.Component}
 * @exported true
 * @description The TAS Positive Pay View Checks desktop content view.
 */
export default function TASPositivePayViewChecksDesktopFormView() {
    const { data, ui, onChange, onClear, onSearch } = useStateManager();
    const checkForms = data.checkForms;
    const hasSearchResults = data.checkForms.length > 0;
    const search = data.search;
    const accountItems = search.accounts.reduce((acc, item) => {
        acc.push(
            <CustomSelectItem key={ item.id } value={ item.id }>{ `${ item.nickname } ${ item.number }` }</CustomSelectItem>
        )
        return acc;
    }, []);

    const statusItems = search.statuses.reduce((acc, item) => {
        acc.push(
            <CustomSelectItem key={ item.id } value={ item.value }>{ item.label }</CustomSelectItem>
        );
        return acc;
    }, []);

    const navigate = useRoute();
    const onAddChecks = evt => {
        navigate("/tasPositivePayAddChecks");
    };
    
    return (
        <div className="show-on-desktop">
            { !ui.search.didSearch.isActive && 
            <div className="flex-container flex-horizontal past-review-cutoff-time-container">
                <div className="set-width curfew-alert">
                    <InfoAlertView text="Please use search to find checks." icon={ <SVGAsset id="InfoIcon" /> } />
                </div>
            </div>
            }
            { ui.search.didSaveChanges.isActive && 
            <div className="flex-container flex-horizontal past-review-cutoff-time-container">
                <div className="set-width success-alert">
                    <InfoAlertView text="The change(s) have been successfully saved." className="checkmark-style"
                    dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Successfully_Saved_Alert_View"
                    icon={ <SVGAsset id="CircleCheckmarkIcon" className="checkmark-icon" /> } />
                </div>
            </div>
            }
            <div>SEARCH</div>
            <FormContainer className="search-form-container">
                <div className="flex-container flex-horizontal">
                    <div className="set-width item">
                        <FormRowView text="Account">
                            <CustomSelect active={ ui.search.account.isActive } value={ search.account.id } dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Account_Select"
                             onChange={ onChange("account") }>
                                { accountItems }
                            </CustomSelect>
                        </FormRowView>
                    </div>
                    <div className="set-width item">
                        <FormRowView text="Payee">
                            <UITextInput value={ search.payee } dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Payee_Input" onChange={ onChange("payee") } />
                        </FormRowView>
                    </div>
                    <div className="set-width item">
                        <FormRowView text="Status">
                            <CustomSelect value={ search.status } active={ ui.search.status.isActive } dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Status_Select"
                             onChange={ onChange("status") }>
                                { statusItems }
                            </CustomSelect>
                        </FormRowView>
                    </div>
                    <div className="set-width item">
                        <FormRowView text="Check Number From">
                            <UITextInput value={ search.checkNumberFrom } dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Check_Number_From_Input"
                            onChange={ onChange("checkNumberFrom") } />
                        </FormRowView>
                    </div>
                    <div className="set-width item">
                        <FormRowView text="Check Number To">
                            <UITextInput value={ search.checkNumberTo } dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Check_Number_To_Input"
                            onChange={ onChange("checkNumberTo") } />
                        </FormRowView>
                    </div>
                </div>
                <div className="flex-container flex-horizontal">
                    <div className="set-width item">
                        <FormRowView text="Issued Date From">
                            <DateSelect value={ search.issuedDateFrom }
                            dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Issued_Date_From"
                            active={ ui.search.issuedDateFrom.isActive } 
                            enablePast={ true } onChange={ onChange("issuedDateFrom") }  />
                        </FormRowView>
                    </div>
                    <div className="set-width item">
                        <FormRowView text="Issued Date To">
                            <DateSelect value={ search.issuedDateTo }
                            dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Issued_Date_To"
                            active={ ui.search.issuedDateTo.isActive }
                            enablePast={ true } onChange={ onChange("issuedDateTo") } />
                        </FormRowView>
                    </div>
                    <div className="set-width item">
                        <FormRowView text="Minimum Amount">
                            <UIAmountInput className="amount-field" value={ search.minimumAmount }
                            showClearButton={ false }
                            dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Minimum_Amount_Input"
                            onChange={ onChange("minimumAmount") } />
                        </FormRowView>
                    </div>
                    <div className="set-width item">
                        <FormRowView text="Maximum Amount">
                            <UIAmountInput className="amount-field" value={ search.maximumAmount }
                            showClearButton={ false }
                            dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Maximum_Amount_Input"
                            onChange={ onChange("maximumAmount") } />
                        </FormRowView>
                    </div>
                    <div className="set-width item"></div>
                </div>
                <div className="flex-container flex-horizontal button-container">
                    <SecondaryButton className="set-width" text="Clear" dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Clear_Button" onClick={ onClear } />
                    <PrimaryButton className="set-width" text="Search" dataPoint="TAS_Positive_Pay_View_Checks_Desktop_Search_Button" onClick={ onSearch } />
                </div>
            </FormContainer>
            { ui.search.didSearch.isActive && 
            <div className="flex-container flex-vertical no-results-content">
                <div className="search-result-message row">{`Search Result (${ checkForms.length })`}</div>
            </div>
             }
            {/* Statically shown until checks are added to the model. */}
            { ui.search.didSearch.isActive && !hasSearchResults &&
            <TASPositivePayViewChecksNoResultsContent onAddChecks={ onAddChecks } />
            }
            { ui.search.didSearch.isActive && hasSearchResults && 
            <TASPositivePayViewChecksDesktopTable/>
            }
        </div>
    )
}